import { ChangeEvent, KeyboardEventHandler, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material//Search';
import {
  Box,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  RadioGroup,
  Typography,
} from '@mui/material';
import { debounce, isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { FinancialStatementPeriodFullWrite } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_COMPANY_TICKERS_TITLE,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_INDEX_TICKERS_TITLE,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NAME_OR_TICKER_TITLE,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NO_FOUND_TITLE,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_ONE_FOUND_TITLE,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_SEARCHING_TITLE,
  getFundInvestmentsGpcDialogMultipleFoundTitle,
} from 'pages/FundInvestments/common/constants';
import {
  COMPANY_OR_TICKER,
  COMPANY_TICKER,
  ENTER_SEARCH_TEXT,
  INDEX_TICKERS,
  MULTIPLE_COMPANIES_FOUND,
  MULTIPLE_TICKERS,
  NO_COMPANIES_FOUND,
  ONE_COMPANY_FOUND,
  SEARCH_BY,
  SEARCH_DELAY,
  SEARCHING,
  SINGLE_COMPANY_RESULTS,
  TICKER_SEPARATORS,
  TICKERS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import ValuationService from 'services/valuations';
import { dbShortDate, getArrayValue, giveFocusToInput, isAllUpperCase } from 'utilities';
import { SearchProps, TickerSearchResponse } from './Search.props';
import { Root, StyledBox, StyledRadio, StyledTextField, StyledTypographyHelperText } from './Search.styles';
import tickerRegexTest from '../../utils/tickerRegexTest';

const Search = (props: SearchProps) => {
  const [{ isShowLoadingProgress }] = useStore() as unknown as UseStoreValues;
  const [searchTicker, setSearchTicker] = useState('');
  const [searchContent, setSearchContent] = useState('');
  const [companyNameResults, setCompanyNameResults] = useState<TickerSearchResponse[] | null>(null);
  const [searchingMessage, setSearchingMessage] = useState('');
  const tickerInputRef = useRef<HTMLInputElement>(null);

  const {
    asOfDate,
    fetchCompanyData,
    fetchPCFromCompGroup,
    financialsPeriods,
    initialValues,
    isFundInvestment,
    marketCurrency,
    marketDate,
    measurementDate,
    multipleTickerSymbols,
    removeCompsData,
    searchCriteria,
    setMultipleTickerSymbols,
    setSearchCriteria,
    setTableData,
  } = props;

  const disabledSearch = useMemo(() => {
    if (isShowLoadingProgress) {
      return true;
    }
    if ([TICKERS, INDEX_TICKERS].includes(searchCriteria)) {
      return !searchTicker || searchTicker.length === 0;
    }

    return true;
  }, [isShowLoadingProgress, searchCriteria, searchTicker]);

  const helperText = useMemo(() => {
    if (companyNameResults) {
      const count = companyNameResults.length;

      if (count === 0) {
        return isFundInvestment ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NO_FOUND_TITLE : NO_COMPANIES_FOUND;
      }

      if (count === 1) {
        return isFundInvestment ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_ONE_FOUND_TITLE : ONE_COMPANY_FOUND;
      }

      // Multiple results
      return isFundInvestment
        ? getFundInvestmentsGpcDialogMultipleFoundTitle(count)
        : `${count} ${MULTIPLE_COMPANIES_FOUND}`;
    }

    if (isShowLoadingProgress) {
      return searchingMessage;
    }

    return isFundInvestment ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_SEARCHING_TITLE : ENTER_SEARCH_TEXT;
  }, [companyNameResults, isShowLoadingProgress, isFundInvestment, searchingMessage]);

  useEffect(() => {
    // Multiple tickers separated by comma, space, or semi-colon
    if (
      [TICKERS, INDEX_TICKERS].includes(searchCriteria)
      && TICKER_SEPARATORS.some(separator => searchContent.includes(separator))
    ) {
      // 'NYSE:SHOP,  NYSE:SQ;NYSE:VEEV; NasdaqGS:WIX, ASDF' -->
      // 'NYSE:SHOP,NYSE:SQ,NYSE:VEEV,NasdaqGS:WIX,ASDF'

      const tickers = searchContent
        .split(/[,\s;]/)
        .filter(ticker => ticker.length !== 0)
        // Add comparable companies with ticker symbols that include shares or securities
        .map(ticker => {
          if (tickerRegexTest(ticker)) {
            return ticker;
          }
          // If the ticker is all uppercase and does not include shares or securities,
          // then we assume the user is searching for that exact symbol.
          return isAllUpperCase(ticker) && !ticker.includes('.')
            ? ticker
            // Otherwise, perform a search for the ticker symbol (before the dot).
            : ticker.split('.')[0];
        });

      setMultipleTickerSymbols(tickers);
    } else {
      setMultipleTickerSymbols([]);
    }
  }, [searchContent, searchCriteria, setMultipleTickerSymbols]);

  // Search by company name or ticker
  const handleCriteriaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCompanyNameResults(null);
    removeCompsData();
    setSearchContent('');
    setMultipleTickerSymbols([]);
    setSearchCriteria(event.target.value);
  };

  useEffect(() => {
    if (tickerInputRef.current) {
      giveFocusToInput(tickerInputRef as RefObject<HTMLInputElement>);
    }
  }, [searchCriteria, tickerInputRef]);

  // The MD may be undefined if the user is performing the search at the firm level
  const handleSearch = async (ticker: string) => {
    setCompanyNameResults(null);
    setSearchingMessage(SEARCHING);

    // Allow us to add comparable companies searching by multiple ticker symbols with Shares or Securities
    const tickerSymbol = tickerRegexTest(ticker) ? ticker : ticker.split('.')[0];

    if (!isShowLoadingProgress) {
      const tickers = multipleTickerSymbols.map(symbol => encodeURIComponent(symbol));
      const isSearchingBySingleCompany = searchCriteria === COMPANY_TICKER;

      if (isSearchingBySingleCompany || !multipleTickerSymbols.length) {
        if (!tickers.includes(tickerSymbol)) {
          tickers.push(tickerSymbol);
          setMultipleTickerSymbols(tickers);
        }
      }

      const periods = getArrayValue(financialsPeriods);
      const md = measurementDate?.id?.toString() ?? '0';

      if (isFundInvestment) {
        // Placeholder value to prevent unnecessary API requests to CapIQ for non-relevant calendar years
        periods.push({ statement_date: dbShortDate(marketDate) } as FinancialStatementPeriodFullWrite);
      }

      if (isSearchingBySingleCompany) {
        // Single ticker or company name
        const companyData = companyNameResults?.find(company => company.ticker_symbol === tickerSymbol);
        const keyword = companyData?.company_id ?? tickerSymbol;
        await fetchCompanyData(keyword, md, marketCurrency, marketDate, periods, asOfDate);
      } else {
        setTableData();

        const isSearchingIndexes = searchCriteria === INDEX_TICKERS;
        await fetchPCFromCompGroup(tickers, md, marketCurrency, marketDate, periods, asOfDate, isSearchingIndexes);
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !!searchTicker) {
      if (multipleTickerSymbols.length === 0) {
        setTableData({
          ...initialValues,
          ticker: `Search ${TICKERS}: ${searchTicker}`,
        });
      }
      handleSearch(searchTicker);
    }
  };

  const debouncedTickerSearch = useRef(
    debounce(async keyword => {
      if (keyword) {
        const valuationSvc = new ValuationService();
        try {
          // Add comparable companies with ticker symbols that include shares or securities
          const searchKeyword = tickerRegexTest(keyword) ? keyword : keyword.split('.')[0];
          const response = (await valuationSvc.getTickerByKeyword(
            encodeURIComponent(searchKeyword)
          )) as TickerSearchResponse[];

          const numberOfResults = response.length;
          setCompanyNameResults(response);
          // Prepare search by clicking the magnifying glass or pressing Enter
          if (numberOfResults === SINGLE_COMPANY_RESULTS) {
            setSearchTicker(response[0].company_id);
          }
        } catch {
          setCompanyNameResults(null);
        }
      }
    }, SEARCH_DELAY)
  ).current;

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearchContent(value);
    if ([TICKERS, INDEX_TICKERS].includes(searchCriteria)) {
      setSearchTicker(value);
    } else {
      // Search by company name
      debouncedTickerSearch(value);
    }
  };

  // Clean helper text when switching between search by ticker and by company name
  useEffect(() => {
    setTableData({ companyName: '', ticker: '', tableRows: [] });
    if ([TICKERS, INDEX_TICKERS].includes(searchCriteria)) {
      setCompanyNameResults(null);
      setSearchContent('');
      setSearchingMessage('');
    }
  }, [searchCriteria, setTableData]);

  return (
    <Root>
      <Typography variant="h3" align="justify">
        {SEARCH_BY}
      </Typography>
      <Box display="flex" justifyContent="start">
        <RadioGroup
          aria-label="searchCriteria"
          name="searchCriteria"
          value={searchCriteria}
          onChange={handleCriteriaChange}>
          <div id="filters" style={{ display: 'flex', flexDirection: 'row', marginBottom: '0.6rem' }}>
            <FormControlLabel
              value={COMPANY_TICKER}
              control={<StyledRadio />}
              label={isFundInvestment ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_NAME_OR_TICKER_TITLE : COMPANY_OR_TICKER}
              disabled={isShowLoadingProgress}
            />
            <FormControlLabel
              value={TICKERS}
              control={<StyledRadio />}
              label={
                isFundInvestment
                  ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_COMPANY_TICKERS_TITLE
                  : MULTIPLE_TICKERS
              }
              disabled={isShowLoadingProgress}
            />
            {isFundInvestment && (
              <FormControlLabel
                value={INDEX_TICKERS}
                control={<StyledRadio />}
                label={FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_MULTIPLE_INDEX_TICKERS_TITLE}
                disabled={isShowLoadingProgress}
              />
            )}
          </div>
        </RadioGroup>
      </Box>
      {isShowLoadingProgress && <LinearProgress color="primary" />}
      <Paper>
        <StyledTextField
          inputRef={tickerInputRef}
          autoFocus
          onChange={handleChange}
          value={searchContent}
          onKeyDown={handleKeyDown as unknown as KeyboardEventHandler}
          label="Search"
          id="gpc-search"
          data-testid="gpc-search"
          type="text"
          name="SearchText"
          variant="outlined"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    data-testid="search-ticker-btn"
                    onClick={() => handleSearch(searchContent)}
                    disabled={disabledSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          disabled={isShowLoadingProgress}
        />
      </Paper>
      {!isEmpty(companyNameResults) && (
        <StyledBox>
          {companyNameResults?.map(option => (
            <MenuItem key={uuid()} value={option.ticker_symbol} onClick={() => handleSearch(option.ticker_symbol)}>
              {`${option.ticker_symbol} - ${option.name}`}
            </MenuItem>
          ))}
        </StyledBox>
      )}
      <StyledTypographyHelperText companyNameResults={companyNameResults}>{helperText}</StyledTypographyHelperText>
    </Root>
  );
};

export default Search;
